import React, { memo } from 'react';
import { FieldValues } from 'react-hook-form';
import cn from 'classnames';

import { Tooltip } from '@components/common';

import { Icons } from '@icons/icons';

import { ISliderCheckboxProps } from './SliderCheckbox.types';

import './SliderCheckbox.scss';

function SliderCheckbox<T extends FieldValues, V extends string> ({
	onChange,
	id,
	label,
	checked,
	disabled,
	className,
	isLabelAfter = false,
	isBold = false,
	name,
	register,
	tooltipText,
	checkboxTooltipText,
}: ISliderCheckboxProps<T, V>) {
	const [isInfoHovered, setIsInfoHovered] = React.useState(false);

	const withUseForm = register && name;

	const labelClassname = cn('f5gSliderCheckboxContainer', className, {
		'f5gSliderCheckboxBorderRadius': !label,
	});
	const textClassname = cn('f5gSliderCheckboxLabel', { 'bold': isBold });

	const handleChange = ({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) => {
		if (onChange) onChange(id, checked);
	};

	const toggleIsInfoHovered = () => {
		setIsInfoHovered(isInfoHovered => !isInfoHovered);
	};

	return (
		<label
			className={labelClassname}
			htmlFor={id}
		>
			{label && !isLabelAfter
				&& <span className={textClassname}>
					{label}
				</span>
			}
			<Tooltip
				content={checkboxTooltipText}
				isDisabled={!checkboxTooltipText}
			>
				<div>
					<label
						className='f5gSliderCheckboxSlider'
						htmlFor={id}
					/>
					{withUseForm
						? <input
							aria-label={label}
							disabled={disabled}
							id={id}
							type='checkbox'
							{...register ? register(name) : {}}
						/>
						: <input
							aria-label={label}
							checked={checked}
							disabled={disabled}
							id={id}
							name='checkbox'
							onChange={handleChange}
							type='checkbox'
						/>
					}
				</div>
			</Tooltip>
			{label && isLabelAfter
				&& <span className={cn(textClassname, 'f5gSliderCheckboxLabelPaddingBefore')}>
					{label}
				</span>
			}
			{!!tooltipText
				&& <span
					className='f5gSliderCheckboxInfoIcon'
					onMouseEnter={toggleIsInfoHovered}
					onMouseLeave={toggleIsInfoHovered}
				>
					<Tooltip content={tooltipText}>
						{Icons.guidelineIcon({ isActive: isInfoHovered })}
					</Tooltip>
				</span>}
		</label>
	);
}

export default memo(SliderCheckbox) as <T extends FieldValues, V extends string>(props: ISliderCheckboxProps<T, V>)
	=> JSX.Element;
